import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";

import { fetchClients } from "../api/clients";
import {
  buildServerFetchParams,
  readClientListQueryParams,
} from "../lib/clientListQuery";
import { messageFromError } from "../../../shared/api/drfErrors";
import ClientCreateModal from "./ClientCreateModal";
import {
  formatLastCommunication,
  formatMatchCount,
  formatOpenTasksCount,
  preferenceListSummary,
} from "../lib/clientListColumns";

// ClientsListScreenContent — cross-screen consistency widget.
//
// Freshness ownership is route-local, ephemeral, manual, passive.
// The stale hint enters via React-Router's location.state — read
// once, consumed once, then explicitly cleared via
// `navigate(..., { replace: true, state: null })`. React Router
// owns the navigation primitive; window.history is not touched.
//
// CL-2 substrate convergence: layout / toolbar / pill / row /
// action grammar delegated to the canonical `.clients-page`
// block in modernize.css.
//
// CL-3 action-grammar collapse: all buttons use the canonical
// `.action-btn` atom. Feature-local classes survive only for
// runtime-topology primitives the canonical design does not
// address (stale strip, refresh-error strip, filter-status
// one-liner, empty-state branches, loading-state container).
//
// CL-4 table-substrate convergence: result region renders the
// canonical `.clients-table-wrap > <table.clients-table>`
// operational grammar in place of the prior `.clients-cards`
// card-feed surface.
//
// CL-4R canonical-slot restoration: the full canonical 10-column
// row is rendered, in canonical order, so the mobile `@media
// (max-width: 991px)` transform in modernize.css (which keys
// `td:nth-of-type(N)::before` pseudo-labels + `grid-template-
// areas` to the 9-`<td>` canonical layout) remains semantically
// aligned. The four cells whose backend semantics are not yet
// exposed on `ClientListSerializer` (Що шукає, Останній контакт,
// Матчі, Задачі) carry explicit structural placeholders — a
// muted em-dash inside the canonical wrapper — with JSX
// comments naming the missing backend transport. This is
// structural slot preservation, NOT semantic simulation: no
// counts, no timestamps, no preference summaries, no urgency
// indicators are synthesized.
//
// CL-5 client-create entrypoint restoration: this widget now
// also owns the canonical .clients-overview > .clients-header
// band (moved here from the screen wrapper) so that the parent
// of the T-1 create-client modal — i.e. this widget — directly
// owns both the .clients-header__actions trigger and the
// `createOpen` state controlling the modal (per R8 §4 T-1).
// Mutation lifecycle lives in ClientCreateForm; post-create
// freshness re-uses this widget's existing manual handleReload
// path — no orchestration, no mayBeStale (we never leave the
// route), no optimistic insertion.
//
// Per CLAUDE.md "Backend-Derivable UI Rule": the placeholder
// cells preserve the design region structurally and explicitly
// mark the absent transport — no fake payloads, no inferred
// lifecycle semantics.

const TYPE_LABELS = {
  buyer: "Покупець",
  seller: "Продавець",
  both: "Обидва",
};

const STATUS_LABELS = {
  lead: "Лід",
  active: "Активний",
  inactive: "Неактивний",
  closed: "Закритий",
};

const ALLOWED_TYPES = ["buyer", "seller", "both"];
const ALLOWED_STATUSES = ["lead", "active", "inactive", "closed"];

const TYPE_CHIPS = [
  { value: "buyer", label: "Покупець" },
  { value: "seller", label: "Продавець" },
  { value: "both", label: "Обидва" },
];

const STATUS_CHIPS = [
  { value: "lead", label: "Лід" },
  { value: "active", label: "Активний" },
  { value: "inactive", label: "Неактивний" },
  { value: "closed", label: "Закритий" },
];

// Deterministic 6-bucket avatar color from client.id. Canonical
// .avatar--{orange,blue,green,purple,rose,teal} variants are defined
// in modernize.css; the bucket choice is purely presentational and
// requires no backend exposure.
const AVATAR_COLORS = ["orange", "blue", "green", "purple", "rose", "teal"];
const avatarColorFor = (client) => {
  const id = client && client.id;
  const n = typeof id === "number" ? id : parseInt(String(id || 0), 10) || 0;
  return AVATAR_COLORS[Math.abs(n) % AVATAR_COLORS.length];
};

// ---- File-local URL helpers (multi-value safe) -----------------------

function writeQueryParamsToUrl(
  navigate,
  pathname,
  query,
  types,
  statuses,
  { hasNewMatches = false, hasOverdueTasks = false } = {},
) {
  const params = new URLSearchParams();
  if (query.trim()) {
    params.set("q", query.trim());
  }
  for (const t of types) {
    params.append("type", t);
  }
  for (const s of statuses) {
    params.append("status", s);
  }
  if (hasNewMatches) {
    params.set("has_new_matches", "true");
  }
  if (hasOverdueTasks) {
    params.set("has_overdue_tasks", "true");
  }
  navigate(
    { pathname, search: params.toString() },
    { replace: true }
  );
}

const formatTime = (epochMs) => {
  if (!epochMs) return "";
  const d = new Date(epochMs);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};

// Canonical "Створено" cell renders DD.MM.YYYY (per design/clients.html
// table sample). Backend exposes `created_at` as an ISO timestamp on
// `ClientListSerializer`; the format is presentational only.
const formatCreatedDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

const unwrapListPayload = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

const fullnameOf = (client) => {
  if (!client) return "";
  if (typeof client.fullname === "string" && client.fullname.trim().length > 0) {
    return client.fullname;
  }
  const composed = [client.first_name, client.last_name]
    .filter((part) => typeof part === "string" && part.trim().length > 0)
    .join(" ")
    .trim();
  return composed || client.phone || "Без імені";
};

const initialsOf = (client) => {
  if (!client) return "?";
  const first = (client.first_name || "").trim();
  const last = (client.last_name || "").trim();
  if (first || last) {
    return `${first[0] || ""}${last[0] || ""}`.toUpperCase() || "?";
  }
  const composed = fullnameOf(client).trim();
  return (composed[0] || "?").toUpperCase();
};

function ClientsListScreenContent() {
  // ---------- Route-local freshness state ----------

  const [clients, setClients] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);

  // R8.7.4 — stale-hint state removed. ClientDetailScreenContent's
  // delete path now emits a success toast instead of a mayBeStale
  // navigation hint, so this screen no longer renders a stale strip on
  // arrival from delete. The edit-notes path (ClientActivityScreenContent)
  // can still emit mayBeStale into the navigation state — that is
  // intentionally unconsumed here (different runtime domain; see R8.7.4
  // Scope A decision: DELETE-only containment). handleReload, refreshing,
  // and refreshError remain — they back the toolbar refresh button and
  // the create-success refetch path, not just the removed stale strip.
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);

  // CL-5 T-1 create-modal open-state. Owned here so the trigger in
  // .clients-header__actions, the modal, and the post-create
  // refetch all live in the same parent — per R8 §4 T-1.
  const [createOpen, setCreateOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // ---------- Route-local query state (one-shot URL hydration) ----------

  const [initialQueryParams] = useState(() =>
    readClientListQueryParams(location.search)
  );
  const [query, setQuery] = useState(initialQueryParams.query);
  const [queryDraft, setQueryDraft] = useState(initialQueryParams.query);
  const [typeSet, setTypeSet] = useState(
    () => new Set(initialQueryParams.types)
  );
  const [statusSet, setStatusSet] = useState(
    () => new Set(initialQueryParams.statuses)
  );
  const [hasNewMatches, setHasNewMatches] = useState(
    () => initialQueryParams.hasNewMatches
  );
  const [hasOverdueTasks, setHasOverdueTasks] = useState(
    () => initialQueryParams.hasOverdueTasks
  );

  const serverFetchParams = useMemo(
    () => buildServerFetchParams({
      query,
      types: Array.from(typeSet),
      statuses: Array.from(statusSet),
      hasNewMatches,
      hasOverdueTasks,
    }),
    [query, typeSet, statusSet, hasNewMatches, hasOverdueTasks],
  );

  useEffect(() => {
    const params = readClientListQueryParams(location.search);
    setQuery(params.query);
    setQueryDraft(params.query);
    setTypeSet(new Set(params.types));
    setStatusSet(new Set(params.statuses));
    setHasNewMatches(params.hasNewMatches);
    setHasOverdueTasks(params.hasOverdueTasks);
  }, [location.search]);

  // ---------- Initial load ----------

  useEffect(() => {
    let cancelled = false;

    setLoadState("loading");
    setLoadError(null);

    fetchClients(serverFetchParams)
      .then((data) => {
        if (cancelled) return;
        setClients(unwrapListPayload(data));
        setLastLoadedAt(Date.now());
        setLoadState("loaded");
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(messageFromError(err));
        setLoadState("error");
      });

    return () => {
      cancelled = true;
    };
  }, [serverFetchParams]);

  const syncUrl = (next) => {
    writeQueryParamsToUrl(
      navigate,
      location.pathname,
      next.query ?? query,
      next.types ?? Array.from(typeSet),
      next.statuses ?? Array.from(statusSet),
      {
        hasNewMatches: next.hasNewMatches ?? hasNewMatches,
        hasOverdueTasks: next.hasOverdueTasks ?? hasOverdueTasks,
      },
    );
  };

  // ---------- Query handlers ----------

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const next = queryDraft;
    setQuery(next);
    writeQueryParamsToUrl(
      navigate,
      location.pathname,
      next,
      Array.from(typeSet),
      Array.from(statusSet),
      { hasNewMatches, hasOverdueTasks },
    );
  };

  const toggleType = (value) => {
    const next = new Set(typeSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setTypeSet(next);
    writeQueryParamsToUrl(
      navigate,
      location.pathname,
      query,
      Array.from(next),
      Array.from(statusSet),
      { hasNewMatches, hasOverdueTasks },
    );
  };

  const toggleStatus = (value) => {
    const next = new Set(statusSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setStatusSet(next);
    writeQueryParamsToUrl(
      navigate,
      location.pathname,
      query,
      Array.from(typeSet),
      Array.from(next),
      { hasNewMatches, hasOverdueTasks },
    );
  };

  const handleClearFilters = () => {
    setQuery("");
    setQueryDraft("");
    setTypeSet(new Set());
    setStatusSet(new Set());
    setHasNewMatches(false);
    setHasOverdueTasks(false);
    writeQueryParamsToUrl(navigate, location.pathname, "", [], [], {
      hasNewMatches: false,
      hasOverdueTasks: false,
    });
  };

  const clearHasNewMatches = () => {
    setHasNewMatches(false);
    syncUrl({ hasNewMatches: false });
  };

  const clearHasOverdueTasks = () => {
    setHasOverdueTasks(false);
    syncUrl({ hasOverdueTasks: false });
  };

  // ---------- Manual reload ----------

  const handleReload = async () => {
    if (refreshing) return;
    setRefreshing(true);
    setRefreshError(null);
    try {
      const data = await fetchClients(serverFetchParams);
      setClients(unwrapListPayload(data));
      setLastLoadedAt(Date.now());
      setLoadState("loaded");
    } catch (err) {
      setRefreshError(messageFromError(err));
    } finally {
      setRefreshing(false);
    }
  };

  // ---------- CL-5 create-modal lifecycle ----------
  //
  // Open/close stays local; post-success path = close + delegate to
  // the existing handleReload (single fetch, same lifecycle as the
  // manual-reload affordance). No optimistic insertion. No
  // mayBeStale — the navigation never leaves /clients.

  const handleOpenCreate = () => setCreateOpen(true);
  const handleCloseCreate = () => setCreateOpen(false);
  const handleCreated = () => {
    setCreateOpen(false);
    void handleReload();
  };

  // ---------- Display rows (server-backed filters) ----------

  const hasActiveFilter =
    Boolean(query.trim())
    || typeSet.size > 0
    || statusSet.size > 0
    || hasNewMatches
    || hasOverdueTasks;

  const totalClientsCount = clients.length;
  const filteredClientsCount = clients.length;

  // ---------- JSX ----------
  //
  // CL-5: the canonical .clients-overview > .clients-header band is
  // now rendered here so the .clients-header__actions create trigger
  // colocates with the createOpen state and the post-create reload.
  // The header is always visible — across loading / error / loaded
  // body branches — so create remains an entrypoint even before the
  // first list fetch resolves.
  //
  // Canonical .clients-workspace substrate. Filters region carries
  // stale-strip + toolbar + filter-status; results region carries
  // refresh-error + empty branches + table. Feature-local classes
  // survive only for runtime topology (R8 L-3 / L-6).

  return (
    <>
      <section className="clients-overview" aria-label="Огляд CRM клієнтів">
        <header className="clients-header">
          <div className="clients-header__text">
            <h1 className="clients-header__title">Клієнти</h1>
            <p className="clients-header__sub">
              Список ваших клієнтів. Після редагування на іншому
              екрані список може застаріти — оновіть його вручну.
            </p>
          </div>
          <div className="clients-header__actions">
            <button
              type="button"
              className="btn-more"
              onClick={handleOpenCreate}
            >
              <i className="fa-solid fa-user-plus" aria-hidden="true" />
              Новий клієнт
            </button>
          </div>
        </header>
      </section>

      {loadState === "loading" ? (
        <div className="feature-clients-list__state">
          <Spinner size="lg" label="Завантаження клієнтів" />
        </div>
      ) : loadState === "error" ? (
        <div className="feature-clients-list__state">
          <EmptyState
            icon="fa-solid fa-triangle-exclamation"
            title="Не вдалося завантажити список клієнтів"
            description={loadError || "Спробуйте оновити сторінку."}
            size="md"
            primaryAction={
              <button
                type="button"
                className="action-btn"
                onClick={handleReload}
                disabled={refreshing}
              >
                {refreshing ? (
                  <Spinner size="sm" label="Оновлення" />
                ) : (
                  "Повторити"
                )}
              </button>
            }
          />
        </div>
      ) : (
        <div className="clients-workspace" data-feature="clients-list">
          <div className="clients-workspace__filters">
        {/* Canonical toolbar substrate. Submit-only search preserves
            the existing route-local lifecycle; chips commit
            immediately (multi-select local filter — no backend
            round-trip, no lifecycle change). */}
        <form
          className="clients-toolbar"
          onSubmit={handleSearchSubmit}
          role="search"
          aria-label="Фільтри списку клієнтів"
        >
          <div className="clients-toolbar__row">
            <label className="clients-search">
              <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
              <input
                type="search"
                name="q"
                value={queryDraft}
                onChange={(event) => setQueryDraft(event.target.value)}
                placeholder="Пошук за іменем, телефоном або email…"
                aria-label="Пошук клієнтів"
              />
            </label>

            <div
              className="clients-filter-group"
              role="group"
              aria-label="Тип клієнта"
            >
              <span className="clients-filter-group__title">Тип</span>
              {TYPE_CHIPS.map((chip) => {
                const on = typeSet.has(chip.value);
                return (
                  <label
                    key={chip.value}
                    className="filter-chip"
                    data-on={on ? "" : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggleType(chip.value)}
                    />
                    <span>{chip.label}</span>
                  </label>
                );
              })}
            </div>

            <div
              className="clients-filter-group"
              role="group"
              aria-label="Статус"
            >
              <span className="clients-filter-group__title">Статус</span>
              {STATUS_CHIPS.map((chip) => {
                const on = statusSet.has(chip.value);
                return (
                  <label
                    key={chip.value}
                    className={`filter-chip filter-chip--${chip.value}`}
                    data-on={on ? "" : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggleStatus(chip.value)}
                    />
                    <span>{chip.label}</span>
                  </label>
                );
              })}
            </div>

            {hasNewMatches || hasOverdueTasks ? (
              <div
                className="clients-filter-group"
                role="group"
                aria-label="CRM фільтри"
              >
                <span className="clients-filter-group__title">CRM</span>
                {hasNewMatches ? (
                  <span className="filter-chip filter-chip--active feature-clients-list__context-chip">
                    <span>Нові матчі</span>
                    <button
                      type="button"
                      className="feature-clients-list__context-chip-remove"
                      onClick={clearHasNewMatches}
                      aria-label="Прибрати фільтр нових матчів"
                    >
                      <i className="fa-solid fa-xmark" aria-hidden="true" />
                    </button>
                  </span>
                ) : null}
                {hasOverdueTasks ? (
                  <span className="filter-chip filter-chip--active feature-clients-list__context-chip">
                    <span>Прострочені задачі</span>
                    <button
                      type="button"
                      className="feature-clients-list__context-chip-remove"
                      onClick={clearHasOverdueTasks}
                      aria-label="Прибрати фільтр прострочених задач"
                    >
                      <i className="fa-solid fa-xmark" aria-hidden="true" />
                    </button>
                  </span>
                ) : null}
              </div>
            ) : null}

            <button type="submit" className="action-btn">
              Знайти
            </button>
            {hasActiveFilter ? (
              <button
                type="button"
                className="action-btn"
                onClick={handleClearFilters}
              >
                Скинути фільтри
              </button>
            ) : null}
            <button
              type="button"
              className="action-btn"
              onClick={handleReload}
              disabled={refreshing}
              aria-label="Оновити список"
              title="Оновити"
            >
              {refreshing ? (
                <Spinner size="sm" label="Оновлення" />
              ) : (
                <i
                  className="fa-solid fa-arrows-rotate"
                  aria-hidden="true"
                />
              )}
            </button>
          </div>
        </form>

        {/* Filter-result + freshness status one-liner — runtime
            topology at subordinate weight. */}
        {clients.length > 0 ? (
          <div className="feature-clients-list__filter-status">
            <span className="feature-clients-list__filter-status-count">
              {hasActiveFilter
                ? `Показано ${filteredClientsCount} з ${totalClientsCount}`
                : `Усього ${totalClientsCount}`}
            </span>
            {lastLoadedAt ? (
              <span className="feature-clients-list__filter-status-timestamp">
                Оновлено: {formatTime(lastLoadedAt)}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="clients-workspace__results">
        {refreshError ? (
          <div className="feature-clients-list__refresh-error" role="alert">
            {refreshError}
          </div>
        ) : null}

        {clients.length === 0 ? (
          <div className="feature-clients-list__empty">
            <EmptyState
              icon="fa-solid fa-address-book"
              title="Поки що немає клієнтів"
              description="Створені вами клієнти з'являться тут."
              size="md"
            />
          </div>
        ) : filteredClientsCount === 0 ? (
          <div className="feature-clients-list__empty feature-clients-list__empty--filtered">
            <EmptyState
              icon="fa-solid fa-filter"
              title="Жоден клієнт не відповідає поточним фільтрам"
              description="Змініть пошук або статус, або скиньте фільтри."
              size="md"
              primaryAction={
                <button
                  type="button"
                  className="action-btn"
                  onClick={handleClearFilters}
                >
                  Скинути фільтри
                </button>
              }
            />
          </div>
        ) : (
          <section
            className="clients-table-wrap"
            aria-label="Список клієнтів"
          >
            <table className="clients-table">
              <thead>
                <tr>
                  <th scope="col">Імʼя</th>
                  <th scope="col">Тип</th>
                  <th scope="col">Статус</th>
                  <th scope="col">Контакти</th>
                  <th scope="col">Що шукає</th>
                  <th scope="col">Останній контакт</th>
                  <th scope="col" className="text-end">Матчі</th>
                  <th scope="col" className="text-end">Задачі</th>
                  <th scope="col">Створено</th>
                  <th scope="col" aria-label="Дії"></th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => {
                  const id = client.id;
                  const fullname = fullnameOf(client);
                  const typeLabel = TYPE_LABELS[client.type] || null;
                  const statusLabel = STATUS_LABELS[client.status] || null;
                  const avatarColor = avatarColorFor(client);
                  const detailHref = `/clients/${id}`;
                  const prefSummary = preferenceListSummary(client);
                  const lastContact = formatLastCommunication(client.last_communication_at);
                  const matchLabel = formatMatchCount(
                    client.matches_count,
                    client.new_matches_count,
                  );
                  const tasksLabel = formatOpenTasksCount(client.open_tasks_count);
                  const createdAt = formatCreatedDate(client.created_at);
                  return (
                    <tr key={id} className="client-row">
                      <th scope="row" className="client-row__name">
                        <Link to={detailHref} className="client-link">
                          <span
                            className={`avatar avatar--${avatarColor}`}
                            aria-hidden="true"
                          >
                            {initialsOf(client)}
                          </span>
                          <span className="client-link__text">
                            <span className="client-link__title">
                              {fullname}
                            </span>
                            {client.source ? (
                              <span className="client-link__meta">
                                {client.source}
                              </span>
                            ) : null}
                          </span>
                        </Link>
                      </th>
                      <td>
                        {typeLabel ? (
                          <span
                            className={`type-pill type-pill--${client.type}`}
                          >
                            {typeLabel}
                          </span>
                        ) : null}
                      </td>
                      <td>
                        {statusLabel ? (
                          <span
                            className={`status-pill status-pill--${client.status}`}
                          >
                            <span className="dot" />
                            <span>{statusLabel}</span>
                          </span>
                        ) : null}
                      </td>
                      <td className="client-row__contacts">
                        {client.phone ? (
                          <a
                            href={`tel:${client.phone}`}
                            className="contact-link"
                          >
                            <i
                              className="fa-solid fa-phone"
                              aria-hidden="true"
                            />
                            <span>{client.phone}</span>
                          </a>
                        ) : null}
                        {client.email ? (
                          <a
                            href={`mailto:${client.email}`}
                            className="contact-link contact-link--mute"
                          >
                            <i
                              className="fa-solid fa-envelope"
                              aria-hidden="true"
                            />
                            <span>{client.email}</span>
                          </a>
                        ) : null}
                      </td>
                      <td className="pref-cell">
                        {prefSummary ? (
                          <span className="pref-cell__summary">{prefSummary}</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="muted">
                        {lastContact ? (
                          <span>{lastContact}</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="text-end">
                        <span>{matchLabel}</span>
                      </td>
                      <td className="text-end">
                        <span>{tasksLabel}</span>
                      </td>
                      <td className="muted">{createdAt}</td>
                      <td className="text-end">
                        <Link
                          to={detailHref}
                          className="row-action"
                          aria-label={`Відкрити клієнта ${fullname}`}
                        >
                          <i
                            className="fa-solid fa-arrow-right"
                            aria-hidden="true"
                          />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        )}
      </div>
        </div>
      )}

      <ClientCreateModal
        open={createOpen}
        onClose={handleCloseCreate}
        onCreated={handleCreated}
      />
    </>
  );
}

export default ClientsListScreenContent;
